package com.dotdrives.web.pages.settings

import com.dotdrives.controller.SettingsController
import com.dotdrives.controller.SettingsUiState
import com.dotdrives.design.DotTone
import com.dotdrives.web.ui.DotBanner
import com.dotdrives.web.ui.DotPill
import com.dotdrives.web.ui.DotSkeleton
import react.FC
import react.Props
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.span
import web.cssom.ClassName
import web.html.ButtonType
import web.html.button as buttonType

external interface BillingPanelProps : Props {
    var controller: SettingsController
    var state: SettingsUiState
}

/** Readable stored billing facts for members; owner/admin actions use the provider panel. */
val BillingPanel = FC<BillingPanelProps> { props ->
    val state = props.state

    state.subscriptionError?.let { error ->
        DotBanner {
            message = error
            onRetry = props.controller::retry
            className = "settings-inline-banner"
        }
    }

    when {
        state.subscriptionLoading && state.subscription == null && !state.subscriptionDenied ->
            DotSkeleton { height = "220px" }
        state.subscriptionDenied -> div {
            className = ClassName("settings-billing-denied")
            // has_perm_strict('billing.read') admits owner/admin/member -- Standard included --
            // and excludes only `field`. "Admins and above" was wrong; "Standard" is this
            // module's own label for `member` (see roleLabel).
            +"Billing details are visible to Standard, Admin, and Owner. Ask a teammate with one of those roles if you need to see plan or payment details."
        }
        state.subscriptionError != null && state.subscription == null -> Unit
        else -> {
            val subscription = state.subscription
            div {
                className = ClassName("settings-billing-card")
                div {
                    className = ClassName("settings-billing-head")
                    div { className = ClassName("settings-billing-title"); +"Subscription" }
                    if (subscription != null) {
                        DotPill { text = subscription.status.uppercase(); tone = statusTone(subscription.status) }
                    }
                }
                div { className = ClassName("settings-billing-plan"); +(subscription?.plan?.replaceFirstChar(Char::uppercase) ?: "—") }
                div {
                    className = ClassName("settings-billing-meta")
                    +billingMetaLine(subscription)
                }
                if (subscription != null) div {
                    className = ClassName("settings-billing-limits")
                    span { +"Seats: ${subscription?.seatsLimit?.toString() ?: "Unlimited"}" }
                    span { +"Givers: ${subscription?.giversLimit?.toString() ?: "Unlimited"}" }
                }

            }
        }
    }
}

private fun statusTone(status: String): DotTone = when (status) {
    "active" -> DotTone.Success
    "trialing" -> DotTone.Info
    "past_due", "canceled" -> DotTone.Danger
    else -> DotTone.Neutral
}

private fun billingMetaLine(subscription: com.dotdrives.model.Subscription?): String {
    if (subscription == null) return "No subscription on file yet"
    val renews = subscription.currentPeriodEnd?.let { "renews ${formatDate(it)}" }
    val trial = subscription.trialEndsAt?.takeIf { subscription.status == "trialing" }?.let { "trial ends ${formatDate(it)}" }
    return listOfNotNull(trial, renews).joinToString(" · ").ifBlank { "No renewal date on file" }
}
